import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { ApiError } from "@/shared/api/http-client";
import { useVenueDetail } from "./use-venue-detail";
import { useVenueQrDataUrl } from "./use-venue-qr";
import { formatVenueLocation } from "./format-venue";

/**
 * Print sheet for a venue's check-in QR: name, address, the QR and a one-line
 * instruction for guests. Routed OUTSIDE the app shell so only the sheet
 * reaches the printer; the toolbar is hidden by `print:hidden`.
 */
export function VenueQrPrintPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const detail = useVenueDetail(id);
  const qr = useVenueQrDataUrl(id);
  const backHref = `/venues-directory/${encodeURIComponent(id ?? "")}`;

  if (detail.isLoading || qr.isLoading) {
    return (
      <PrintLayout backHref={backHref} backLabel={t("venuesDirectory.qr.print.back")}>
        <p data-testid="venue-qr-print-loading" className="font-sans text-sm text-muted-foreground">
          {t("venuesDirectory.qr.print.loading")}
        </p>
      </PrintLayout>
    );
  }

  if (detail.isError || qr.isError || !detail.data) {
    const qrStatus = qr.error instanceof ApiError ? qr.error.status : undefined;
    const copy =
      qrStatus === 400
        ? t("venuesDirectory.qr.print.notActive")
        : t("venuesDirectory.qr.print.error");
    return (
      <PrintLayout backHref={backHref} backLabel={t("venuesDirectory.qr.print.back")}>
        <p data-testid="venue-qr-print-error" className="font-sans text-sm text-destructive">
          {copy}
        </p>
      </PrintLayout>
    );
  }

  const venue = detail.data;
  return (
    <PrintLayout
      backHref={backHref}
      backLabel={t("venuesDirectory.qr.print.back")}
      onPrint={() => window.print()}
      printLabel={t("venuesDirectory.qr.print.printButton")}
    >
      <article
        data-testid="venue-qr-print-sheet"
        className="mx-auto flex max-w-xl flex-col items-center gap-6 rounded-lg border border-border bg-card p-10 text-center print:border-0 print:bg-white print:p-0"
      >
        <p className="brand-eyebrow">{t("venuesDirectory.qr.print.eyebrow")}</p>
        <h1 className="text-3xl">{venue.name}</h1>
        <p className="font-sans text-sm text-muted-foreground">{formatVenueLocation(venue)}</p>
        <img
          data-testid="venue-qr-print-image"
          src={qr.data}
          alt={t("venuesDirectory.qr.print.imageAlt", { name: venue.name })}
          className="h-72 w-72"
        />
        <p className="font-sans text-base text-foreground">
          {t("venuesDirectory.qr.print.instructions")}
        </p>
      </article>
    </PrintLayout>
  );
}

interface PrintLayoutProps {
  readonly backHref: string;
  readonly backLabel: string;
  readonly onPrint?: () => void;
  readonly printLabel?: string;
  readonly children: React.ReactNode;
}

function PrintLayout({ backHref, backLabel, onPrint, printLabel, children }: PrintLayoutProps) {
  return (
    <main className="min-h-screen bg-background p-6 print:bg-white print:p-0">
      <div className="mx-auto mb-6 flex max-w-xl items-center justify-between print:hidden">
        <Link
          to={backHref}
          data-testid="venue-qr-print-back"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
        {onPrint && (
          <Button type="button" onClick={onPrint} data-testid="venue-qr-print-button">
            <Printer className="mr-2 h-4 w-4" />
            {printLabel}
          </Button>
        )}
      </div>
      {children}
    </main>
  );
}
